import React, { useCallback, useEffect } from 'react'
import {
  ProcessEventData,
  ProcessModelContainerProps
} from './ProcessModelContainer.types'
import { useTranslation } from 'react-i18n-lite'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { useSelector } from 'react-redux'
import { AuthenticationType } from 'authentication/shared/types'
import { useSearchParams } from 'react-router-dom'
import useClientTracking from 'shared/hooks/useClientTracking'
import { useIframe } from 'shared/hooks'
import Iframe from 'shared/components/Iframe'

function generateIframeUrl(
  processmodelUuid: string,
  workflowengineUrl: string,
  iframeQueryString: string,
  tokenUuid?: string
) {
  const baseUrl = tokenUuid
    ? `${workflowengineUrl}token/${tokenUuid}`
    : `${workflowengineUrl}processmodel/new/${processmodelUuid}`

  const fullUrl = `${baseUrl}?${iframeQueryString}`

  return fullUrl
}

function ProcessModelContainer({
  props: { styleMap, tokenUuid, processmodelUuid, returnOnFinish, returnUrl }
}: ProcessModelContainerProps) {
  const { language } = useTranslation()

  const styleProps = useStyleMaps(styleMap)

  const [searchParams] = useSearchParams()

  let dataContainerQueryString = ''
  const denyKeys = ['tokenUuid', 'processmodelUuid']
  searchParams.forEach((value, key) => {
    if (!denyKeys.includes(key)) {
      dataContainerQueryString = `${dataContainerQueryString}&${key}=${value}`
    }
  })

  const token = useSelector(
    ({ authentication }: AuthenticationType) => authentication.token
  )

  const workflowengineUrl = globalThis.waWorkflowengineUrl

  const { clientTracking, clearClientTracking } = useClientTracking()

  const iframeQueryString = `token=${token}&lang=${language}&clientTracking=${JSON.stringify(clientTracking)}${dataContainerQueryString}`

  const iframeUrl = generateIframeUrl(
    processmodelUuid || '',
    workflowengineUrl,
    iframeQueryString,
    tokenUuid
  )

  const { iframeAttributes, receiveMessage } = useIframe({
    origin: iframeUrl,
    src: iframeUrl,
    interactionReturnUrl: returnUrl,
    returnOnFinishedInteraction: returnOnFinish
  })

  const onNewProcess = useCallback(
    (eventData: ProcessEventData) => {
      const currentUrl = new URL(globalThis.location.href)

      currentUrl.searchParams.delete('processmodelUuid')
      currentUrl.searchParams.set('tokenUuid', eventData.tokenUuid)

      globalThis.history.replaceState(
        globalThis.history.state,
        '',
        currentUrl.href
      )
      clearClientTracking()
    },
    [clearClientTracking]
  )

  const onReceivedMessage = useCallback(
    (event: MessageEvent) => {
      event.data.tokenUuid && onNewProcess(event.data)
      receiveMessage(event)
    },
    [onNewProcess, receiveMessage]
  )

  useEffect(() => {
    globalThis.addEventListener('message', onReceivedMessage)
    return () => globalThis.removeEventListener('message', onReceivedMessage)
  }, [onReceivedMessage])

  return (
    <>
      {(processmodelUuid || tokenUuid) && iframeAttributes ? (
        <Iframe
          key={iframeAttributes.src}
          {...iframeAttributes}
          style={styleProps}
        />
      ) : null}
    </>
  )
}

export default ProcessModelContainer
